/**
 * Repeating millisecond timer with optional auto-stop after N ticks.
 *
 * Mirrors the legacy `Timer` class from `@suntex.classic/utils`. Used by
 * classic modules to drive countdown clocks and tick-based animation.
 *
 * Usage:
 * ```ts
 * const timer = new Timer(1000, () => updateDisplay(timer.remainingTicks()));
 * timer.setTimeout(() => alert('done'), 20);
 * timer.start();
 * ```
 */
export class Timer {
  private readonly tickInterval: number;
  private readonly onTick: (() => void) | null;
  private tickLimit = 0;
  private onTimeout: (() => void) | null = null;
  private count = 0;
  private timerId = 0;
  private running = false;

  constructor(tickInterval = 1000, onTick?: () => void) {
    this.tickInterval = tickInterval;
    this.onTick = onTick ?? null;
  }

  /**
   * Schedule a timeout callback that fires after `tickLimit` ticks once
   * the timer is started.
   */
  setTimeout(onTimeout: (() => void) | null, tickLimit = 1): void {
    this.onTimeout = onTimeout;
    this.tickLimit = tickLimit;
  }

  /** Start (or restart) ticking. */
  start(): void {
    this.stop();
    this.count = 0;
    this.running = true;
    this.timerId = window.setInterval(() => {
      this.count += 1;
      this.onTick?.();
      if (this.tickLimit > 0 && this.remainingTicks() <= 0) {
        this.stop();
        this.onTimeout?.();
      }
    }, this.tickInterval);
  }

  /** Stop ticking and clear the underlying interval. */
  stop(): void {
    if (this.timerId) {
      window.clearInterval(this.timerId);
      this.timerId = 0;
    }
    this.running = false;
  }

  isRunning(): boolean {
    return this.running;
  }

  /** Number of ticks elapsed since the last `start()`. */
  currentCount(): number {
    return this.count;
  }

  /** Number of ticks remaining before the timeout callback fires. */
  remainingTicks(): number {
    return this.tickLimit - this.count;
  }
}
